import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import {
  Box,
  Typography,
  useTheme
} from "@mui/material";
import BulletList from "../../components/layout/BulletList";

const mme = [
  "When a MME is completely done morphing, the resulting items are normal tradeable items and will not morph again.",
  "If you trade your MME before it is done morphing, you will NOT get any stages back. The user who owns the MME when it reaches the final stage will receive all the stages. If you do not want a particular stage and want to trade only that stage away, wait for it to be done morphing!",
  "Different IDs signify different paths so some traders may request a certain path. If you are not sure what ID corresponds to which path, ask! Also see next section for more info on how to check.",
  "When viewing MME reports, check carefully if you are looking at the first stage (in the format MME##-S1). Reports for stage 1 often include trades during the time it was buyable in the mall as an unmorphed MME. After a MME is done morphing, the reports may be outdated.",
  "If there is an active MME reaching its last stage, give it some time if you have not received all your items! Don't worry if you see that other users received all stages back first - the final morphs may take a couple hours to get through every user.",
  "Current stages are typically placed whereever you kept the MME and prior MME stages are typically awarded into your inventory. If you don't see them, try checking your closet/sdb/gallery/pet customizations first!",
];

const sections = [
  { id: "top", title: "Top" },
  { id: "know-before-trading", title: "Know Before Trading" },
  { id: "mme-paths", title: "MME Paths" },
  { id: "checking-mme-path-in-mall", title: "Checking MME Path in Mall" },
  { id: "checking-mme-ids", title: "Checking MME IDs" },
  { id: "bonuses", title: "Bonuses" },
];

const MME = () => {
  const theme = useTheme();

  return (
    <GuideLayout sections={sections}>
      <Section id="top" title="Mysterious Morphing Experiment">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: August 2026
        </Typography>
        A MME (Mysterious Morphing Experiment) is a fun item that changes after purchase. A MME is buyable in the NC Mall (first stage) for about a month before it is no longer sold. If you purchase or trade for one, it will temporarily morph into a different item after it retires from the mall. Throughout the next week or two, the item will morph through a few stages (typically 4-5). When a MME is done morphing, all stages will appear in your inventory as individual items, and if you're lucky, you may receive a bonus stage!


      </Section>

      <Section id="know-before-trading" title="Know Before Trading">
        <BulletList items={mme} />

        <Typography paragraph>
          You can tell if a MME is finished morphing by the icons. If there is a question mark icon, it is an active experiment and will continue to morph. If you see a regular closet hanger icon, that means it is a regular item and will not morph.
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/icon_diff.png')} alt="example of mme32 path a and b" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>
      </Section>

      <Section id="mme-paths" title="MME Paths">

        MMEs typically have different paths it can morph into, and these are determined by their IDs. For example, MME32 had two paths. If you got only one path A MME, then you would get back the first stage and only the items on the left at the end of the morph. For bonuses, it does not matter which path MME you have; it is the same bonus for all paths and your chances to receive a bonus are not affected by path.

        {(theme.palette.mode === "light") ? <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/mme_paths_light_mode.png')} alt="example of mme32 path a and b" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box> : <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/mme_paths_dark_mode.png')} alt="example of mme32 path a and b" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>
        }
        <Typography paragraph>
          There are MMEs (4-5 stages) and MiniMMEs (2 stages). This article focuses more on what you need to know for trading MMEs like checking IDs and uses MME32 and MME33 for examples. MiniMMEs paths/IDs can be checked with the same method. Just know that recent MMEs have 2 available paths, and we have not had a MiniMMEs for quite some time now (over 10 years!), but historically MiniMMEs have 3 paths available.
        </Typography>
      </Section>

      <Section id="checking-mme-path-in-mall" title="Checking MME Path in Mall">
        <Typography paragraph>
          If you wanted to get all stages of the MME, you would have to buy different paths. You can check and choose your MME ID / path before you purchase by checking your cart page. When you click add to cart, you have a random chance at getting any path. When you view your cart, different item IDs (indicating different paths) will show up as separate rows. It is recommended that you add a couple MMEs to your cart, and then adjust to the desired quantity after viewing the cart and before checking out.
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_1.png')} alt="adding mmes to cart visual" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_2.png')} alt="mme paths in cart visual. top is path A, bottom is path B" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>

        <Typography paragraph>
          For example, if you only want to buy path B, you would need to add both paths to cart to verify that you have a path B (bottom one) in cart. Then you would remove the top row from your cart and check out.
        </Typography>

      </Section>

      <Section id="checking-mme-ids" title="Checking MME IDs">
        <Typography paragraph>
          If your MMEs are already purchased, you can check if you have the same or different paths in your inventory. MMEs of the same path/ID will stack. If your MMEs do not stack, then you have different paths. Be sure that the stack setting is on for this! You will not find the specific IDs in your inventory. If you need to send a specific ID to a user, it is recommended that you deposit all your MMEs, verify the ID you need to send from your SDB, and then move the one to your inventory to send. Having multiple MMEs in your inventory makes it easy for mix-ups to occur, so save yourself the trouble!
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_3.png')} alt="mme paths in cart visual. top is path A, bottom is path B" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>

        <Typography paragraph>
          To find the item IDs in your SDB, you will need to open up dev tools. It is easiest to do this on desktop, where you right click the action checkbox and click inspect. Dev tools should then open with the checkbox element line highlighted. Look at the id="sdb-chk-#####" as that will be your item ID. You may see users shorten it to the last two digits, so for the example below, it would be referred to as MME ID 72. Note: The images below use Chrome, but this works on other browsers like Firefox too.
        </Typography>
        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_4.png')} alt="mme paths in cart visual. top is path A, bottom is path B" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_5.png')} alt="mme paths in sdb visual" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_6.png')} alt="mme paths in sdb visual - extra verification by highlighting the element line" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>

        <Typography paragraph>
          Like the cart in the NC Mall, if you have different MMEs IDs in your SDB, it is in sequential order. The earlier ID is commonly referred to as Path A, so for this example, MME ID 72 is path A and MME ID 73 is path B. If you are unsure what IDs are available / which paths they correspond to, ask! Clear communication is what makes a trade successful.
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/mme/id_step_7.png')} alt="mme paths in sdb visual. top is path A, bottom is path B" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>
      </Section>

      <Section id="bonuses" title="Bonuses">
        <Typography paragraph>
          There is no way to guarantee getting a MME bonus. The MME bonus is the same for all paths, and there is no path that has a higher or lower chance. If you want a MME bonus, just hope you are lucky. <b>There is no official drop rate for MME bonuses.</b> It is suspected to be around 20-25% based on user collected data, but there is no confirmation by TNT. Some users buy many MMEs in hopes of obtaining a bonus, but at the end of the day, it is completely based on luck.
        </Typography>
      </Section>

    </GuideLayout>
  );
};

export default MME;
